import FilterDrawer from 'components/CourseDashboard/FilterDrawer';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useStripe } from 'contexts/stripe';
import { useViewport } from 'contexts/viewport';
import _ from 'lodash';
import Desktop from 'pages/CourseDashboard/desktop';
import Mobile from 'pages/CourseDashboard/mobile';
import Tablet from 'pages/CourseDashboard/tablet';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import courseService from 'services/course';
import { Drawer } from 'shell/components';
import { CourseLobbyLayout } from 'utils/constants';

const PageLayout = () => {
    const { currentUser, selectAccount, startCourseSetup, updateCurrentUser } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { setJoyrideRun, setJoyrideStepIndex, setJoyridePaused } = useJoyride();
    const { t } = useTranslation('course');
    const location = useLocation();
    const { message } = useSnackbar();
    const stripeContext = useStripe();
    const { device } = useViewport();

    const [courses, setCourses] = useState(null);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [sortKey, setSortKey] = useState('titlea-z');
    const [searchBarInput, setSearchBarInput] = useState('');
    const [viewArchived, setViewArchived] = useState(false);
    const [courseLobbyLayout, setCourseLobbyLayout] = useState(CourseLobbyLayout.GRID);

    const handleCourseCardDropdown = ({ key, courseId }) => {
        const courseObj = courses.find((course) => course.course_id === courseId);
        switch (key) {
            case 'duplicate-course': {
                if (courseObj.finished_course_set_up === false && courseObj?.role === 'instructor')
                    return message.error(
                        t('course-lobby.message.course-incomplete-setup', 'Please finish course set up')
                    );
                return startCourseSetup(courseObj);
            }
            case 'delete': {
                return openDeleteCourseDialog(courseObj);
            }
            case 'archive': {
                return toggleArchiveCourse(courseObj);
            }
            default:
                return;
        }
    };

    const toggleArchiveCourse = async (course) => {
        const updatedArchiveState = !course.is_archived;
        setCourses((prev) =>
            prev.map((courseItem) =>
                courseItem.course_id === course.course_id
                    ? { ...courseItem, is_archived: updatedArchiveState }
                    : courseItem
            )
        );
        await courseService.updateCourse(course.course_id, { is_archived: updatedArchiveState });
        return message.success(
            updatedArchiveState
                ? t('course-lobby.message.course-archived-success', 'Course was archived successfully.')
                : t('course-lobby.message.course-unarchived-success', 'Course was unarchived successfully.')
        );
    };

    const deleteCourse = async (courseId) => {
        setCourses((prev) => prev.filter((course) => course.course_id !== courseId));
        const deletingCourse = courses.find((course) => course.course_id === courseId);
        await courseService.deleteCourse(courseId);
        return message.success(
            t('course-lobby.message.course-deleted-success', '{{courseName}} was deleted successfully.', {
                courseName: _.capitalize(deletingCourse.name) || 'The new course',
            })
        );
    };

    const openDeleteCourseDialog = (course) => {
        setDialog({
            open: true,
            message: t('dialog.course-delete-confirmation.title', 'Delete {{courseName}}?', {
                courseName: _.capitalize(course.name) || 'this course',
            }),
            description: t(
                'dialog.course-delete-confirmation.description',
                'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                return deleteCourse(course.course_id);
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    };

    const onChangeSearchBarInput = (value) => {
        if (value === '' || !value) {
            setSearchBarInput('');
        } else {
            setSearchBarInput(value);
        }
    };

    const handleFilterClick = (key, data) => {
        switch (key) {
            case 'SORT': {
                return setSortKey(data);
            }
            default:
                return;
        }
    };

    const handleResetFilterSort = () => {
        setSortKey('titlea-z');
    };

    const coursesInView = useMemo(() => {
        return courses?.filter((course) => {
            return viewArchived === !!course.is_archived;
        });
    }, [courses, viewArchived]);

    const getSortedFilteredcourses = () => {
        return coursesInView
            ?.filter((course) => {
                if (searchBarInput) {
                    return course.name?.toLowerCase().includes(searchBarInput.toLowerCase());
                }
                return true;
            })
            .sort((a, b) => {
                if (sortKey === 'titlea-z') {
                    return a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1;
                }
                if (sortKey === 'titlez-a') {
                    return a.name?.toLowerCase() < b.name?.toLowerCase() ? 1 : -1;
                }

                return 0;
            });
    };

    const handleCourseLobbyLayoutChange = async (layout) => {
        if (!Object.values(CourseLobbyLayout).includes(layout)) return;

        const previousLayout = courseLobbyLayout;
        setCourseLobbyLayout(layout);

        try {
            await updateCurrentUser({
                preferences: {
                    ...currentUser?.preferences,
                    course_lobby_layout: layout,
                },
            });
        } catch (e) {
            console.error(e);
            setCourseLobbyLayout(previousLayout);
        }
    };

    useEffect(() => {
        const savedLayout = currentUser?.preferences?.course_lobby_layout;

        if (Object.values(CourseLobbyLayout).includes(savedLayout)) {
            setCourseLobbyLayout(savedLayout);
            return;
        }

        setCourseLobbyLayout(CourseLobbyLayout.GRID);
    }, [currentUser?.preferences?.course_lobby_layout]);

    useEffect(() => {
        (async () => {
            const { courses, accounts } = await courseService.listCourses();
            const data = accounts
                .filter(({ role }) => role !== 'student')
                .map((account) => {
                    const course = courses
                        .filter(({ active }) => active !== false) // some old course documents created from self serve admin are missing `active`
                        .find(({ id }) => id === account.course_id);
                    return {
                        course_id: course.id,
                        name: course.name,
                        cover_color: course.cover_color,
                        generated_id: course.generated_id,
                        owner_account_id: account.id,
                        role: account.role,
                        is_archived: course.is_archived,
                    };
                });
            setCourses(data);
        })();
    }, []);

    useEffect(() => {
        if (currentUser?.finished_onboarding) return;
        if (stripeContext?.paymentDialog?.open) return;

        if (location.state?.showWelcomeDialog) {
            stripeContext.setWelcomeDialog(true);
            setJoyridePaused(true);
        } else {
            if (courses) {
                setJoyrideRun(true);
                setJoyridePaused(false);
            }
            if (courses?.length) {
                setJoyrideStepIndex(1);
            }
        }
    }, [
        courses,
        currentUser?.finished_onboarding,
        location.state?.showWelcomeDialog,
        setJoyridePaused,
        setJoyrideRun,
        setJoyrideStepIndex,
        stripeContext,
        stripeContext?.paymentDialog?.open,
        stripeContext.setWelcomeDialog,
    ]);

    useEffect(() => {
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    const filterKey = useMemo(() => {
        return [sortKey !== 'titlea-z' && 'sort'].filter(Boolean);
    }, [sortKey]);

    const props = {
        courses,
        filteredCourses: getSortedFilteredcourses(),
        startCourseSetup,
        searchBarInput,
        filterKey,
        onChangeSearchBarInput,
        setIsFilterDrawerOpen,
        selectCourse: (course) => selectAccount(course.owner_account_id),
        handleCourseCardDropdown,
        viewArchived,
        setViewArchived,
        hasCoursesInView: !!coursesInView?.length,
        courseLobbyLayout,
        setCourseLobbyLayout: handleCourseLobbyLayoutChange,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    const filterDrawerProps = {
        open: isFilterDrawerOpen,
        onClose: () => setIsFilterDrawerOpen(false),
    };

    const FilterDrawerInner = (
        <FilterDrawer
            sortKey={sortKey}
            onClick={handleFilterClick}
            onClose={() => setIsFilterDrawerOpen(false)}
            resetFilterSort={handleResetFilterSort}
        />
    );

    const renderResponsiveFilterDrawers = (device) => {
        return {
            mobile: (
                <Drawer anchor={'left'} device={'mobile'} {...filterDrawerProps}>
                    {FilterDrawerInner}
                </Drawer>
            ),
            tablet: (
                <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                    {FilterDrawerInner}
                </Drawer>
            ),
            desktop: (
                <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                    {FilterDrawerInner}
                </Drawer>
            ),
        }[device];
    };

    return (
        <>
            {renderResponsiveLayout(device)}
            {renderResponsiveFilterDrawers(device)}
        </>
    );
};

export default PageLayout;
